import AddRoundedIcon from '@mui/icons-material/AddRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import { Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import React, { ChangeEvent, Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { deleteProgram, loadPrograms } from '../../actions/programs';
import { ProcessProgramResult, processProgram } from '../../actions/workouts';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import DeleteProgramDialog from './DeleteProgramDialog';
import ProgramListTable from './ProgramListTable';
import ProcessProgramDialog from './ProcessProgramDialog';
import { ProgramManagementListItem } from './programs.types';

interface ComponentProps {}

interface ReduxProps {
    deletingProgramName?: string;
    items: ProgramManagementListItem[];
    loadingList: boolean;
    processingProgram: boolean;
}

interface ReduxActionProps {
    deleteProgram: (name: string) => Promise<{ message?: string; success: boolean }>;
    loadPrograms: () => void;
    processProgram: (programKey: string) => Promise<ProcessProgramResult>;
}

interface State {
    pendingDeleteProgramName?: string;
    pendingProcessProgramName?: string;
    searchTerm: string;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class ProgramManagementPage extends Component<Props, State> {
    state: State = {
        searchTerm: '',
    };

    componentDidMount() {
        this.props.loadPrograms();
    }

    getFilteredItems() {
        const normalizedSearchTerm = this.state.searchTerm.trim().toLowerCase();

        if (!normalizedSearchTerm) {
            return this.props.items;
        }

        return this.props.items.filter((item) => item.name.includes(normalizedSearchTerm));
    }

    getPendingDeleteItem() {
        return this.props.items.find((item) => item.name === this.state.pendingDeleteProgramName);
    }

    getPendingProcessItem() {
        return this.props.items.find((item) => item.name === this.state.pendingProcessProgramName);
    }

    onCloseDeleteDialog = () => {
        this.setState({ pendingDeleteProgramName: undefined });
    };

    onConfirmDelete = async () => {
        const { pendingDeleteProgramName } = this.state;

        if (!pendingDeleteProgramName) {
            return;
        }

        const result = await this.props.deleteProgram(pendingDeleteProgramName);

        if (result.success) {
            this.setState({ pendingDeleteProgramName: undefined });
        }
    };

    onCloseProcessDialog = () => {
        if (this.props.processingProgram) {
            return;
        }

        this.setState({ pendingProcessProgramName: undefined });
    };

    onConfirmProcess = async () => {
        const pendingProcessItem = this.getPendingProcessItem();

        if (!pendingProcessItem) {
            return;
        }

        const result = await this.props.processProgram(pendingProcessItem.name);

        if (result.success) {
            this.setState({ pendingProcessProgramName: undefined });
        }
    };

    onCreateProgram = () => {
        this.props.navigate('/programs/new');
    };

    onImportProgram = () => {
        this.props.navigate('/programs/new?import=1');
    };

    onEditProgram = (name: string) => {
        this.props.navigate(`/programs/${name}/edit`);
    };

    onOpenDeleteDialog = (name: string) => {
        this.setState({ pendingDeleteProgramName: name });
    };

    onOpenProcessDialog = (name: string) => {
        this.setState({ pendingProcessProgramName: name });
    };

    onOpenProgram = (name: string) => {
        this.props.navigate(`/programs/${name}`);
    };

    onSearchChanged = (event: ChangeEvent<HTMLInputElement>) => {
        this.setState({ searchTerm: event.target.value });
    };

    render() {
        const { deletingProgramName, items, loadingList, processingProgram } = this.props;
        const filteredItems = this.getFilteredItems();
        const pendingDeleteItem = this.getPendingDeleteItem();
        const pendingProcessItem = this.getPendingProcessItem();
        const fullyConfiguredCount = items.filter((item) => item.status === 'Complete').length;

        if (loadingList && !items.length) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Box maxWidth={1280}>
                <Box sx={{ alignItems: { md: 'center', xs: 'stretch' }, display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1.5, justifyContent: 'space-between', mb: 2.5 }}>
                    <Box>
                        <Typography sx={{ fontSize: { md: '1.6rem', xs: '1.4rem' }, fontWeight: 700, mb: 0.25 }} variant="h4">Programs</Typography>
                        <Typography color="text.secondary" sx={{ maxWidth: 760 }} variant="body1">
                            Build, review, and maintain week-by-week program schedules.
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, gap: 1 }}>
                        <Button onClick={this.onImportProgram} startIcon={<UploadFileRoundedIcon />} variant="outlined">Import CSV/TSV</Button>
                        <Button onClick={this.onCreateProgram} startIcon={<AddRoundedIcon />} variant="contained">Create Program</Button>
                    </Box>
                </Box>
                <Paper sx={{ borderRadius: 3, mb: 2.5, p: 2 }} variant="outlined">
                    <TextField fullWidth label="Search Programs" onChange={this.onSearchChanged} size="small" value={this.state.searchTerm} />
                </Paper>
                {!filteredItems.length && (
                    <Paper sx={{ borderRadius: 3, p: 5, textAlign: 'center' }} variant="outlined">
                        <Typography sx={{ fontWeight: 700, mb: 1 }} variant="h6">No Programs Yet</Typography>
                        <Typography color="text.secondary" sx={{ mb: 2 }} variant="body2">
                            Create your first program to start building week-by-week workout schedules.
                        </Typography>
                        <Button onClick={this.onCreateProgram} startIcon={<AddRoundedIcon />} variant="contained">Create First Program</Button>
                    </Paper>
                )}
                {filteredItems.length > 0 && (
                    <ProgramListTable
                        deletingProgramName={deletingProgramName}
                        items={filteredItems}
                        onDelete={this.onOpenDeleteDialog}
                        onEdit={this.onEditProgram}
                        onOpen={this.onOpenProgram}
                        onProcess={this.onOpenProcessDialog}
                        processingProgram={processingProgram}
                        processingProgramName={processingProgram ? pendingProcessItem?.name : undefined}
                    />
                )}
                <Paper sx={{ borderRadius: 3, mt: 2.5, p: 2 }} variant="outlined">
                    <Typography sx={{ fontWeight: 700, mb: 1 }} variant="subtitle1">Summary</Typography>
                    <Typography variant="body2">Total Programs: {items.length}</Typography>
                    <Typography variant="body2">Fully Configured: {fullyConfiguredCount}</Typography>
                    <Typography variant="body2">With Gaps: {Math.max(items.length - fullyConfiguredCount, 0)}</Typography>
                </Paper>
                <DeleteProgramDialog
                    deleting={deletingProgramName === pendingDeleteItem?.name}
                    name={pendingDeleteItem?.name}
                    onCancel={this.onCloseDeleteDialog}
                    onConfirm={this.onConfirmDelete}
                    totalDays={pendingDeleteItem?.totalDays}
                    totalWeeks={pendingDeleteItem?.totalWeeks}
                    visible={Boolean(pendingDeleteItem)}
                />
                <ProcessProgramDialog
                    displayName={pendingProcessItem?.displayName}
                    live={pendingProcessItem?.live}
                    name={pendingProcessItem?.name}
                    onCancel={this.onCloseProcessDialog}
                    onConfirm={this.onConfirmProcess}
                    processing={processingProgram}
                    totalDays={pendingProcessItem?.totalDays}
                    totalWeeks={pendingProcessItem?.totalWeeks}
                    visible={Boolean(pendingProcessItem)}
                />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ misc, programs }: ReduxState) => ({
    deletingProgramName: programs.deletingProgramName,
    items: programs.items,
    loadingList: programs.loadingList,
    processingProgram: misc.processingProgram,
});

const mapDispatchToProps: any = {
    deleteProgram,
    loadPrograms,
    processProgram,
};

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, mapDispatchToProps)(withRouter(ProgramManagementPage));